import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Container } from './ui/Container'
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card'
import { Button } from './ui/Button'
import { toast } from 'sonner'
import {
  Heart,
  Brain,
  Wind,
  Moon,
  Sun,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  Clock,
  Target,
  CheckCircle,
  Sparkles,
  Activity
} from 'lucide-react'

interface YogaPose {
  id: string
  name: string
  sanskrit: string
  duration: number
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  benefits: string[]
  instructions: string[]
  category: 'stress' | 'sleep' | 'energy' | 'flexibility' | 'strength' | 'balance'
  image: string
  breathing: string
  contraindications: string[]
}

interface YogaSession {
  id: string
  name: string
  description: string
  duration: number
  poses: string[]
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  focus: 'stress' | 'sleep' | 'energy' | 'flexibility' | 'strength' | 'balance'
  timeOfDay: 'morning' | 'evening' | 'anytime'
  image: string
  benefits: string[]
}

const yogaPoses: YogaPose[] = [
  {
    id: '1',
    name: 'Child\'s Pose',
    sanskrit: 'Balasana',
    duration: 60,
    difficulty: 'beginner',
    benefits: ['Reduces stress', 'Calms the mind', 'Relieves back pain', 'Stretches hips'],
    instructions: ['Kneel on the floor', 'Sit back on your heels', 'Fold forward with arms extended', 'Rest forehead on ground', 'Breathe deeply'],
    category: 'stress',
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800',
    breathing: 'Deep, slow breaths',
    contraindications: ['Knee injuries', 'Pregnancy (late term)']
  },
  {
    id: '2',
    name: 'Mountain Pose',
    sanskrit: 'Tadasana',
    duration: 30,
    difficulty: 'beginner',
    benefits: ['Improves posture', 'Increases awareness', 'Reduces flat feet', 'Relieves back pain'],
    instructions: ['Stand with feet hip-width apart', 'Ground through all four corners of feet', 'Engage thigh muscles', 'Lengthen spine', 'Relax shoulders'],
    category: 'balance',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800',
    breathing: 'Natural breathing, focus on grounding',
    contraindications: ['Severe balance issues']
  },
  {
    id: '3',
    name: 'Downward Dog',
    sanskrit: 'Adho Mukha Svanasana',
    duration: 45,
    difficulty: 'beginner',
    benefits: ['Strengthens arms', 'Stretches hamstrings', 'Improves circulation', 'Relieves back pain'],
    instructions: ['Start on hands and knees', 'Lift hips up and back', 'Straighten legs gently', 'Press hands firmly into ground', 'Relax head between arms'],
    category: 'flexibility',
    image: 'https://images.unsplash.com/photo-1588280991492-4914101ea333?auto=format&fit=crop&q=80&w=800',
    breathing: 'Deep, steady breaths',
    contraindications: ['Carpal tunnel syndrome', 'High blood pressure', 'Pregnancy']
  },
  {
    id: '4',
    name: 'Warrior I',
    sanskrit: 'Virabhadrasana I',
    duration: 60,
    difficulty: 'intermediate',
    benefits: ['Strengthens legs', 'Opens hips', 'Improves balance', 'Builds confidence'],
    instructions: ['Step one foot back', 'Bend front knee 90 degrees', 'Raise arms overhead', 'Square hips forward', 'Hold strong stance'],
    category: 'strength',
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&q=80&w=800',
    breathing: 'Deep, powerful breaths',
    contraindications: ['Knee problems', 'Hip issues', 'High blood pressure']
  }
]

const yogaSessions: YogaSession[] = [
  {
    id: '1',
    name: 'Morning Energy Flow',
    description: 'Start your day with energizing poses and mindful breathing',
    duration: 15,
    poses: ['2', '3', '4'],
    difficulty: 'beginner',
    focus: 'energy',
    timeOfDay: 'morning',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800',
    benefits: ['Increased energy', 'Improved focus', 'Better mood', 'Enhanced flexibility']
  },
  {
    id: '2',
    name: 'Evening Stress Relief',
    description: 'Unwind and release tension with calming restorative poses',
    duration: 20,
    poses: ['1', '3'],
    difficulty: 'beginner',
    focus: 'stress',
    timeOfDay: 'evening',
    image: 'https://images.unsplash.com/photo-1552077038-f865aec8bde4?auto=format&fit=crop&q=80&w=800',
    benefits: ['Reduced stress', 'Better sleep', 'Muscle relaxation', 'Mental clarity']
  },
  {
    id: '3',
    name: 'Flexibility & Balance',
    description: 'Improve your range of motion and stability through focused practice',
    duration: 25,
    poses: ['2', '3', '4'],
    difficulty: 'intermediate',
    focus: 'flexibility',
    timeOfDay: 'anytime',
    image: 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&q=80&w=800',
    benefits: ['Enhanced flexibility', 'Better balance', 'Improved posture', 'Core strength']
  }
]

const YogaSection: React.FC = () => {
  const [selectedSession, setSelectedSession] = useState<YogaSession | null>(null)
  const [currentPoseIndex, setCurrentPoseIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [completedPoses, setCompletedPoses] = useState<string[]>([])

  const handleComingSoon = (feature: string) => {
    toast.info(`🚀 ${feature} - Feature Coming Soon!`, {
      duration: 3000,
      position: 'top-center',
    });
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (isPlaying && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handlePoseComplete()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isPlaying, timeRemaining])

  const handleStartSession = (session: YogaSession) => {
    setSelectedSession(session)
    setCurrentPoseIndex(0)
    setCompletedPoses([])
    const firstPose = yogaPoses.find(pose => pose.id === session.poses[0])
    if (firstPose) {
      setTimeRemaining(firstPose.duration)
    }
  }

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const handlePoseComplete = () => {
    if (!selectedSession) return
    
    const currentPose = yogaPoses.find(pose => pose.id === selectedSession.poses[currentPoseIndex])
    if (currentPose) {
      setCompletedPoses(prev => [...prev, currentPose.id])
    }
    
    if (currentPoseIndex < selectedSession.poses.length - 1) {
      const nextPoseIndex = currentPoseIndex + 1
      setCurrentPoseIndex(nextPoseIndex)
      const nextPose = yogaPoses.find(pose => pose.id === selectedSession.poses[nextPoseIndex])
      if (nextPose) {
        setTimeRemaining(nextPose.duration)
      }
    } else {
      // Session complete
      setIsPlaying(false)
    }
  }

  const handleReset = () => {
    setCurrentPoseIndex(0)
    setIsPlaying(false)
    setCompletedPoses([])
    if (selectedSession) {
      const firstPose = yogaPoses.find(pose => pose.id === selectedSession.poses[0])
      if (firstPose) {
        setTimeRemaining(firstPose.duration)
      }
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getDifficultyColor = (difficulty: string) => {
    const colors = {
      beginner: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      advanced: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    }
    return colors[difficulty as keyof typeof colors]
  }

  const getFocusIcon = (focus: string) => {
    const icons = {
      stress: <Heart className="w-5 h-5" />,
      sleep: <Moon className="w-5 h-5" />,
      energy: <Sun className="w-5 h-5" />,
      flexibility: <Wind className="w-5 h-5" />,
      strength: <Target className="w-5 h-5" />,
      balance: <Activity className="w-5 h-5" />
    }
    return icons[focus as keyof typeof icons] || <Heart className="w-5 h-5" />
  }

  const currentPose = selectedSession ? yogaPoses.find(pose => pose.id === selectedSession.poses[currentPoseIndex]) : null

  return (
    <section className="py-20 bg-theme-bg content-section">
      <Container>
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-6">
              Mindful Yoga Practice
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto leading-relaxed">
              Discover personalized yoga sessions designed for your wellness goals. From stress relief to improved flexibility, 
              find the perfect practice for your mind and body.
            </p>
          </motion.div>
        </div>

        {/* Yoga Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-8 text-center">Recommended Sessions</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {yogaSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full flex flex-col hover:shadow-lg transition-all duration-300 cursor-pointer bg-theme-surface/70 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 rounded-xl group">
                  <CardHeader className="pb-4">
                    <div className="mb-4">
                      <img 
                        src={session.image} 
                        alt={session.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg text-theme-primary group-hover:text-blue-400">{session.name}</CardTitle>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(session.difficulty)}`}>
                        {session.difficulty}
                      </div>
                    </div>
                    <p className="text-sm text-theme-secondary group-hover:text-blue-400 mb-3">{session.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-theme-secondary group-hover:text-blue-400">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{session.duration} min</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {getFocusIcon(session.focus)}
                        <span className="capitalize">{session.focus}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col">
                    <div className="space-y-2 mb-4">
                      {session.benefits.slice(0, 3).map((benefit, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span className="text-theme-secondary">{benefit}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full mt-auto bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700"
                      onClick={() => handleStartSession(session)}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Session
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Yoga Player */}
        <AnimatePresence>
          {selectedSession && currentPose && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="bg-theme-surface/90 rounded-2xl p-8 border border-blue-200 dark:border-blue-700 shadow-xl"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-theme-primary mb-2">{selectedSession.name}</h3>
                <div className="flex items-center justify-center space-x-4 text-sm text-theme-secondary">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(timeRemaining)} remaining</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Target className="w-4 h-4" />
                    <span>Pose {currentPoseIndex + 1} of {selectedSession.poses.length}</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Current Pose */}
                <div>
                  <div className="mb-4">
                    <img 
                      src={currentPose.image} 
                      alt={currentPose.name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <div className="text-center mb-4">
                    <h4 className="text-xl font-bold text-theme-primary">{currentPose.name}</h4>
                    <p className="text-theme-secondary italic">{currentPose.sanskrit}</p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <h5 className="font-semibold text-theme-primary mb-2 flex items-center">
                        <Wind className="w-4 h-4 mr-2 text-blue-500" />
                        Breathing
                      </h5>
                      <p className="text-sm text-theme-secondary">{currentPose.breathing}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-theme-primary mb-2">Instructions</h5>
                      <ol className="space-y-1">
                        {currentPose.instructions.map((instruction, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-theme-secondary">
                            <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mt-0.5">
                              {idx + 1}
                            </span>
                            <span>{instruction}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Pose Benefits & Controls */}
                <div>
                  <div className="space-y-4 mb-6">
                    <div>
                      <h5 className="font-semibold text-theme-primary mb-2 flex items-center">
                        <Heart className="w-4 h-4 mr-2 text-green-500" />
                        Benefits
                      </h5>
                      <ul className="space-y-1">
                        {currentPose.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center space-x-2 text-sm text-theme-secondary">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {currentPose.contraindications.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-theme-primary mb-2 flex items-center">
                          <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                          Contraindications
                        </h5>
                        <ul className="space-y-1">
                          {currentPose.contraindications.map((contra, idx) => (
                            <li key={idx} className="text-sm text-amber-600 dark:text-amber-400">
                              • {contra}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Controls */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-4">
                      <Button
                        size="lg"
                        onClick={handlePlayPause}
                        className="flex items-center space-x-2"
                      >
                        {isPlaying ? (
                          <>
                            <Pause className="w-5 h-5" />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-5 h-5" />
                            <span>Play</span>
                          </>
                        )}
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={handleReset}
                        className="flex items-center space-x-2"
                      >
                        <RotateCcw className="w-5 h-5" />
                        <span>Reset</span>
                      </Button>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-theme-secondary">Session Progress</span>
                        <span className="font-semibold text-theme-primary">
                          {Math.round((completedPoses.length / selectedSession.poses.length) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-theme-surface/60 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-teal-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${(completedPoses.length / selectedSession.poses.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Benefits Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {[
            { icon: <Heart className="w-8 h-8 text-blue-500" />, title: 'Stress Relief', desc: 'Reduce cortisol and anxiety' },
            { icon: <Moon className="w-8 h-8 text-teal-500" />, title: 'Better Sleep', desc: 'Improve sleep quality' },
            { icon: <Wind className="w-8 h-8 text-blue-400" />, title: 'Flexibility', desc: 'Increase range of motion' },
            { icon: <Brain className="w-8 h-8 text-teal-500" />, title: 'Mental Clarity', desc: 'Enhance focus and mindfulness' }
          ].map((benefit, index) => (
            <div key={index} className="text-center p-6 bg-theme-surface/70 rounded-xl border border-theme-border group">
              <div className="flex justify-center mb-4">{benefit.icon}</div>
              <h4 className="font-bold text-theme-primary group-hover:text-blue-400 mb-2">{benefit.title}</h4>
              <p className="text-sm text-theme-secondary group-hover:text-blue-400">{benefit.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center bg-theme-surface/80 border-2 border-blue-200 dark:border-blue-700 rounded-2xl p-8 text-theme-text"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-3xl font-bold mb-4 text-theme-primary">Start Your Yoga Journey</h3>
          <p className="text-xl mb-8 text-theme-secondary max-w-2xl mx-auto">
            Experience the transformative power of yoga with personalized sessions tailored to your wellness goals. 
            From beginner to advanced, find your perfect practice.
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-blue-600 to-teal-500 text-white hover:from-blue-700 hover:to-teal-600"
            onClick={() => handleComingSoon('Yoga Practice')}
          >
            Begin Your Practice
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </Container>
    </section>
  )
}

export default YogaSection
